import {
  AgentIdSchema,
  AgentSkillIdSchema,
  AssetIdSchema,
  ElementIdSchema,
  McpServerIdSchema,
  ToolIdSchema,
} from '@dpem/ontology';
import * as z from 'zod';

import {
  AutomationLevelSchema,
  CatalogKindSchema,
  McpTransportSchema,
  PromotionStateSchema,
} from '../common/enums.js';
import { PageQuerySchema, paginated } from '../common/pagination.js';
import {
  DescriptionSchema,
  ExternalUrlSchema,
  IdentifierSchema,
  IsoDateTimeSchema,
  LabelSchema,
  MarkdownSchema,
  QueryIntSchema,
  SentenceSchema,
  ShortTextSchema,
  TagListSchema,
  TextFilterSchema,
  queryList,
  sortBy,
} from '../common/primitives.js';
import { AttachmentSchema, ExternalLinkSchema } from './attachments.js';

/**
 * catalog context — the five reusable aggregates methods reference by id
 * (architecture §2). Catalog entities carry ontology ids (`tool:jira`) because
 * an activity binding stores exactly that id and nothing else.
 */

export const CATALOG_SORT_FIELDS = ['name', 'updatedAt', 'createdAt', 'usageCount'] as const;

/** Read-side fields every catalog entry shares. */
const catalogReadBase = {
  /**
   * The entry's row uuid, which is what `attachments` and `external-links` mean by
   * `ownerId`.
   *
   * `id` above is the ontology id (`asset:hld-checklist`) and it is the identity the
   * domain, the URLs and every method binding use. But the attachments context stores
   * its owner as a uuid — it has to, because an activity's ontology id is unique only
   * within its method version, so a uuid is the only handle that is unambiguous across
   * all seven owner kinds. Without this field a client holding a catalog entry has no
   * way to name it as an owner, which is exactly the hole that made "Add link" and
   * "Upload file" fail with `ownerId must be the UUID of the owning record`.
   */
  recordId: IdentifierSchema,
  name: LabelSchema,
  description: DescriptionSchema.nullable(),
  writeup: MarkdownSchema.nullable(),
  category: LabelSchema.nullable(),
  tags: TagListSchema,
  attachments: z.array(AttachmentSchema),
  links: z.array(ExternalLinkSchema),
  /** How many activities across all method versions reference this entry. */
  usageCount: z.int().min(0),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  createdBy: ShortTextSchema,
  /**
   * Which catalogue this entry belongs to: `'GLOBAL'` for the platform's, or the
   * uuid of the adoption session whose client brought it.
   *
   * On every catalogue response and not only the session-scoped ones, deliberately.
   * The bindings editor has to group *Declared by this method version* apart from
   * *Brought by this client*, and it draws those two groups from two different
   * endpoints — a provenance field that only one of them carried would force the UI
   * to infer origin from which request an entry arrived on, which is exactly the
   * kind of inference that renders a client's tool as though the platform shipped it.
   */
  scopeId: ShortTextSchema,
  /**
   * The platform entry this one stands in for, when a client re-minted an id the
   * platform also holds. Recorded, never acted on: a shadow makes the substitute
   * offerable in the bindings editor, it rebinds nothing.
   */
  standsInFor: ElementIdSchema.nullable(),
  /** The client that brought it — the byline the origin rail shows. */
  broughtBy: LabelSchema.nullable(),
} as const;

/** Write-side fields every catalog entry shares. */
const catalogWriteBase = {
  name: LabelSchema,
  description: DescriptionSchema.optional(),
  writeup: MarkdownSchema.optional(),
  category: LabelSchema.optional(),
  tags: TagListSchema.optional(),
} as const;

export const ToolSchema = z.strictObject({
  id: ToolIdSchema,
  kind: z.literal('tool'),
  ...catalogReadBase,
  vendor: LabelSchema.nullable(),
  docsUrl: ExternalUrlSchema.nullable(),
});
export type Tool = z.infer<typeof ToolSchema>;

export const AssetSchema = z.strictObject({
  id: AssetIdSchema,
  kind: z.literal('asset'),
  ...catalogReadBase,
  /** Free-form because a client's asset taxonomy is theirs, not ours. */
  assetType: LabelSchema.nullable(),
  format: ShortTextSchema.nullable(),
});
export type Asset = z.infer<typeof AssetSchema>;

export const AgentSchema = z.strictObject({
  id: AgentIdSchema,
  kind: z.literal('agent'),
  ...catalogReadBase,
  automation: AutomationLevelSchema,
  capabilities: z.array(ShortTextSchema).max(64),
  skillIds: z.array(AgentSkillIdSchema).max(64),
  mcpServerIds: z.array(McpServerIdSchema).max(64),
  modelHint: ShortTextSchema.nullable(),
});
export type Agent = z.infer<typeof AgentSchema>;

export const AgentSkillSchema = z.strictObject({
  id: AgentSkillIdSchema,
  kind: z.literal('agentSkill'),
  ...catalogReadBase,
  capability: DescriptionSchema.nullable(),
  /** The skill's own instruction body, versioned by the catalog's updatedAt. */
  instructions: MarkdownSchema.nullable(),
  toolIds: z.array(ToolIdSchema).max(64),
});
export type AgentSkill = z.infer<typeof AgentSkillSchema>;

export const McpServerSchema = z.strictObject({
  id: McpServerIdSchema,
  kind: z.literal('mcpServer'),
  ...catalogReadBase,
  transport: McpTransportSchema,
  /** `stdio` servers carry a command instead of an endpoint; exactly one is set. */
  endpoint: ExternalUrlSchema.nullable(),
  command: ShortTextSchema.nullable(),
  capabilities: z.array(ShortTextSchema).max(64),
  authMode: z.enum(['none', 'apiKey', 'oauth', 'sigv4']).nullable(),
});
export type McpServer = z.infer<typeof McpServerSchema>;

export const CatalogEntrySchema = z.discriminatedUnion('kind', [
  ToolSchema,
  AssetSchema,
  AgentSchema,
  AgentSkillSchema,
  McpServerSchema,
]);
export type CatalogEntry = z.infer<typeof CatalogEntrySchema>;

/* ── Tool CRUD ─────────────────────────────────────────────────────────── */

const toolWriteExtras = {
  vendor: LabelSchema.optional(),
  docsUrl: ExternalUrlSchema.optional(),
} as const;

export const CreateToolRequestSchema = z.strictObject({
  id: ToolIdSchema.optional(),
  ...catalogWriteBase,
  ...toolWriteExtras,
});
export type CreateToolRequest = z.infer<typeof CreateToolRequestSchema>;
export const CreateToolResponseSchema = z.strictObject({ tool: ToolSchema });
export type CreateToolResponse = z.infer<typeof CreateToolResponseSchema>;

export const UpdateToolRequestSchema = z.strictObject({
  toolId: ToolIdSchema,
  ...catalogWriteBase,
  ...toolWriteExtras,
});
export type UpdateToolRequest = z.infer<typeof UpdateToolRequestSchema>;
export const UpdateToolResponseSchema = z.strictObject({ tool: ToolSchema });
export type UpdateToolResponse = z.infer<typeof UpdateToolResponseSchema>;

export const GetToolRequestSchema = z.strictObject({ toolId: ToolIdSchema });
export type GetToolRequest = z.infer<typeof GetToolRequestSchema>;
export const GetToolResponseSchema = z.strictObject({ tool: ToolSchema });
export type GetToolResponse = z.infer<typeof GetToolResponseSchema>;

export const ListToolsRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  ...TextFilterSchema.shape,
  ...sortBy(CATALOG_SORT_FIELDS).shape,
  category: LabelSchema.optional(),
});
export type ListToolsRequest = z.infer<typeof ListToolsRequestSchema>;
export const ListToolsResponseSchema = paginated(ToolSchema);
export type ListToolsResponse = z.infer<typeof ListToolsResponseSchema>;

/** Refused while the entry is still referenced, unless `force` is set. */
export const DeleteCatalogEntryQuery = {
  force: z.stringbool().optional(),
} as const;

export const DeleteToolRequestSchema = z.strictObject({
  toolId: ToolIdSchema,
  ...DeleteCatalogEntryQuery,
});
export type DeleteToolRequest = z.infer<typeof DeleteToolRequestSchema>;
export const DeleteCatalogEntryResponseSchema = z.strictObject({
  id: ElementIdSchema,
  kind: CatalogKindSchema,
  deletedAt: IsoDateTimeSchema,
  /** Activities that referenced the entry and were unbound by the delete. */
  unboundActivityCount: z.int().min(0),
});
export type DeleteCatalogEntryResponse = z.infer<typeof DeleteCatalogEntryResponseSchema>;
export const DeleteToolResponseSchema = DeleteCatalogEntryResponseSchema;
export type DeleteToolResponse = z.infer<typeof DeleteToolResponseSchema>;

/* ── Asset CRUD ────────────────────────────────────────────────────────── */

const assetWriteExtras = {
  assetType: LabelSchema.optional(),
  format: ShortTextSchema.optional(),
} as const;

export const CreateAssetRequestSchema = z.strictObject({
  id: AssetIdSchema.optional(),
  ...catalogWriteBase,
  ...assetWriteExtras,
});
export type CreateAssetRequest = z.infer<typeof CreateAssetRequestSchema>;
export const CreateAssetResponseSchema = z.strictObject({ asset: AssetSchema });
export type CreateAssetResponse = z.infer<typeof CreateAssetResponseSchema>;

export const UpdateAssetRequestSchema = z.strictObject({
  assetId: AssetIdSchema,
  ...catalogWriteBase,
  ...assetWriteExtras,
});
export type UpdateAssetRequest = z.infer<typeof UpdateAssetRequestSchema>;
export const UpdateAssetResponseSchema = z.strictObject({ asset: AssetSchema });
export type UpdateAssetResponse = z.infer<typeof UpdateAssetResponseSchema>;

export const GetAssetRequestSchema = z.strictObject({ assetId: AssetIdSchema });
export type GetAssetRequest = z.infer<typeof GetAssetRequestSchema>;
export const GetAssetResponseSchema = z.strictObject({ asset: AssetSchema });
export type GetAssetResponse = z.infer<typeof GetAssetResponseSchema>;

export const ListAssetsRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  ...TextFilterSchema.shape,
  ...sortBy(CATALOG_SORT_FIELDS).shape,
  assetType: LabelSchema.optional(),
});
export type ListAssetsRequest = z.infer<typeof ListAssetsRequestSchema>;
export const ListAssetsResponseSchema = paginated(AssetSchema);
export type ListAssetsResponse = z.infer<typeof ListAssetsResponseSchema>;

export const DeleteAssetRequestSchema = z.strictObject({
  assetId: AssetIdSchema,
  ...DeleteCatalogEntryQuery,
});
export type DeleteAssetRequest = z.infer<typeof DeleteAssetRequestSchema>;
export const DeleteAssetResponseSchema = DeleteCatalogEntryResponseSchema;
export type DeleteAssetResponse = z.infer<typeof DeleteAssetResponseSchema>;

/* ── Agent CRUD ────────────────────────────────────────────────────────── */

const agentWriteExtras = {
  automation: AutomationLevelSchema,
  capabilities: z.array(ShortTextSchema).max(64).optional(),
  skillIds: z.array(AgentSkillIdSchema).max(64).optional(),
  mcpServerIds: z.array(McpServerIdSchema).max(64).optional(),
  modelHint: ShortTextSchema.optional(),
} as const;

export const CreateAgentRequestSchema = z.strictObject({
  id: AgentIdSchema.optional(),
  ...catalogWriteBase,
  ...agentWriteExtras,
});
export type CreateAgentRequest = z.infer<typeof CreateAgentRequestSchema>;
export const CreateAgentResponseSchema = z.strictObject({ agent: AgentSchema });
export type CreateAgentResponse = z.infer<typeof CreateAgentResponseSchema>;

export const UpdateAgentRequestSchema = z.strictObject({
  agentId: AgentIdSchema,
  ...catalogWriteBase,
  ...agentWriteExtras,
});
export type UpdateAgentRequest = z.infer<typeof UpdateAgentRequestSchema>;
export const UpdateAgentResponseSchema = z.strictObject({ agent: AgentSchema });
export type UpdateAgentResponse = z.infer<typeof UpdateAgentResponseSchema>;

export const GetAgentRequestSchema = z.strictObject({ agentId: AgentIdSchema });
export type GetAgentRequest = z.infer<typeof GetAgentRequestSchema>;
export const GetAgentResponseSchema = z.strictObject({ agent: AgentSchema });
export type GetAgentResponse = z.infer<typeof GetAgentResponseSchema>;

export const ListAgentsRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  ...TextFilterSchema.shape,
  ...sortBy(CATALOG_SORT_FIELDS).shape,
  automation: AutomationLevelSchema.optional(),
});
export type ListAgentsRequest = z.infer<typeof ListAgentsRequestSchema>;
export const ListAgentsResponseSchema = paginated(AgentSchema);
export type ListAgentsResponse = z.infer<typeof ListAgentsResponseSchema>;

export const DeleteAgentRequestSchema = z.strictObject({
  agentId: AgentIdSchema,
  ...DeleteCatalogEntryQuery,
});
export type DeleteAgentRequest = z.infer<typeof DeleteAgentRequestSchema>;
export const DeleteAgentResponseSchema = DeleteCatalogEntryResponseSchema;
export type DeleteAgentResponse = z.infer<typeof DeleteAgentResponseSchema>;

/* ── AgentSkill CRUD ───────────────────────────────────────────────────── */

const agentSkillWriteExtras = {
  capability: DescriptionSchema.optional(),
  instructions: MarkdownSchema.optional(),
  toolIds: z.array(ToolIdSchema).max(64).optional(),
} as const;

export const CreateAgentSkillRequestSchema = z.strictObject({
  id: AgentSkillIdSchema.optional(),
  ...catalogWriteBase,
  ...agentSkillWriteExtras,
});
export type CreateAgentSkillRequest = z.infer<typeof CreateAgentSkillRequestSchema>;
export const CreateAgentSkillResponseSchema = z.strictObject({ agentSkill: AgentSkillSchema });
export type CreateAgentSkillResponse = z.infer<typeof CreateAgentSkillResponseSchema>;

export const UpdateAgentSkillRequestSchema = z.strictObject({
  agentSkillId: AgentSkillIdSchema,
  ...catalogWriteBase,
  ...agentSkillWriteExtras,
});
export type UpdateAgentSkillRequest = z.infer<typeof UpdateAgentSkillRequestSchema>;
export const UpdateAgentSkillResponseSchema = z.strictObject({ agentSkill: AgentSkillSchema });
export type UpdateAgentSkillResponse = z.infer<typeof UpdateAgentSkillResponseSchema>;

export const GetAgentSkillRequestSchema = z.strictObject({ agentSkillId: AgentSkillIdSchema });
export type GetAgentSkillRequest = z.infer<typeof GetAgentSkillRequestSchema>;
export const GetAgentSkillResponseSchema = z.strictObject({ agentSkill: AgentSkillSchema });
export type GetAgentSkillResponse = z.infer<typeof GetAgentSkillResponseSchema>;

export const ListAgentSkillsRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  ...TextFilterSchema.shape,
  ...sortBy(CATALOG_SORT_FIELDS).shape,
  agentId: AgentIdSchema.optional(),
});
export type ListAgentSkillsRequest = z.infer<typeof ListAgentSkillsRequestSchema>;
export const ListAgentSkillsResponseSchema = paginated(AgentSkillSchema);
export type ListAgentSkillsResponse = z.infer<typeof ListAgentSkillsResponseSchema>;

export const DeleteAgentSkillRequestSchema = z.strictObject({
  agentSkillId: AgentSkillIdSchema,
  ...DeleteCatalogEntryQuery,
});
export type DeleteAgentSkillRequest = z.infer<typeof DeleteAgentSkillRequestSchema>;
export const DeleteAgentSkillResponseSchema = DeleteCatalogEntryResponseSchema;
export type DeleteAgentSkillResponse = z.infer<typeof DeleteAgentSkillResponseSchema>;

/* ── McpServer CRUD ────────────────────────────────────────────────────── */

const mcpServerWriteExtras = {
  transport: McpTransportSchema,
  endpoint: ExternalUrlSchema.optional(),
  command: ShortTextSchema.optional(),
  capabilities: z.array(ShortTextSchema).max(64).optional(),
  authMode: z.enum(['none', 'apiKey', 'oauth', 'sigv4']).optional(),
} as const;

export const CreateMcpServerRequestSchema = z.strictObject({
  id: McpServerIdSchema.optional(),
  ...catalogWriteBase,
  ...mcpServerWriteExtras,
});
export type CreateMcpServerRequest = z.infer<typeof CreateMcpServerRequestSchema>;
export const CreateMcpServerResponseSchema = z.strictObject({ mcpServer: McpServerSchema });
export type CreateMcpServerResponse = z.infer<typeof CreateMcpServerResponseSchema>;

export const UpdateMcpServerRequestSchema = z.strictObject({
  mcpServerId: McpServerIdSchema,
  ...catalogWriteBase,
  ...mcpServerWriteExtras,
});
export type UpdateMcpServerRequest = z.infer<typeof UpdateMcpServerRequestSchema>;
export const UpdateMcpServerResponseSchema = z.strictObject({ mcpServer: McpServerSchema });
export type UpdateMcpServerResponse = z.infer<typeof UpdateMcpServerResponseSchema>;

export const GetMcpServerRequestSchema = z.strictObject({ mcpServerId: McpServerIdSchema });
export type GetMcpServerRequest = z.infer<typeof GetMcpServerRequestSchema>;
export const GetMcpServerResponseSchema = z.strictObject({ mcpServer: McpServerSchema });
export type GetMcpServerResponse = z.infer<typeof GetMcpServerResponseSchema>;

export const ListMcpServersRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  ...TextFilterSchema.shape,
  ...sortBy(CATALOG_SORT_FIELDS).shape,
  transport: McpTransportSchema.optional(),
});
export type ListMcpServersRequest = z.infer<typeof ListMcpServersRequestSchema>;
export const ListMcpServersResponseSchema = paginated(McpServerSchema);
export type ListMcpServersResponse = z.infer<typeof ListMcpServersResponseSchema>;

export const DeleteMcpServerRequestSchema = z.strictObject({
  mcpServerId: McpServerIdSchema,
  ...DeleteCatalogEntryQuery,
});
export type DeleteMcpServerRequest = z.infer<typeof DeleteMcpServerRequestSchema>;
export const DeleteMcpServerResponseSchema = DeleteCatalogEntryResponseSchema;
export type DeleteMcpServerResponse = z.infer<typeof DeleteMcpServerResponseSchema>;

/* ── Cross-catalog search ──────────────────────────────────────────────── */

export const SearchCatalogRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  ...TextFilterSchema.shape,
  ...sortBy(CATALOG_SORT_FIELDS).shape,
  kinds: queryList(CatalogKindSchema, z.array(CatalogKindSchema).max(5)).optional(),
  /** Restrict to entries referenced by a given method version. */
  usedByMethodVersionId: IdentifierSchema.optional(),
  minUsageCount: QueryIntSchema.optional(),
});
export type SearchCatalogRequest = z.infer<typeof SearchCatalogRequestSchema>;
export const SearchCatalogResponseSchema = z.strictObject({
  ...paginated(CatalogEntrySchema).shape,
  /** Counts per kind for the authoring palette's live catalogue badges. */
  countsByKind: z.record(CatalogKindSchema, z.int().min(0)),
});
export type SearchCatalogResponse = z.infer<typeof SearchCatalogResponseSchema>;

/* ── Session-scoped catalogue ──────────────────────────────────────────────
 *
 * One adoption's own entries: the tools, assets, agents, skills and MCP servers a
 * client brings to the session where their method is being tailored. The rows are
 * ordinary catalogue rows carrying `scopeId = <session uuid>`, which is why the
 * response schemas below are the *same* `CatalogEntry` shapes the platform
 * catalogue serves — a client's tool is a tool, and giving it a parallel read model
 * would mean every consumer had two of everything.
 *
 * Addressed under `/workshops/:sessionId/...` because the session is what owns the
 * scope and what authorises the write. One generic route per verb rather than five
 * per kind: the platform catalogue's per-kind routes exist because each kind has
 * its own facets and its own list screen, and a session catalogue is one screen
 * with a kind sub-tab.
 */

/** The write side of all five kinds, kind-discriminated. */
export const SessionCatalogueDraftSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('tool'), ...catalogWriteBase, ...toolWriteExtras }),
  z.strictObject({ kind: z.literal('asset'), ...catalogWriteBase, ...assetWriteExtras }),
  z.strictObject({ kind: z.literal('agent'), ...catalogWriteBase, ...agentWriteExtras }),
  z.strictObject({ kind: z.literal('agentSkill'), ...catalogWriteBase, ...agentSkillWriteExtras }),
  z.strictObject({ kind: z.literal('mcpServer'), ...catalogWriteBase, ...mcpServerWriteExtras }),
]);
export type SessionCatalogueDraft = z.infer<typeof SessionCatalogueDraftSchema>;
/**
 * The same draft as a *caller* builds it: ids are plain strings rather than branded ones.
 *
 * A form produces `string[]` for `skillIds` and the brand is what parsing adds, so the type a
 * browser builds the request in is the input side. Named here rather than restated as
 * `z.input<…>` at each call site, which would put zod in surfaces that only need the shape.
 */
export type SessionCatalogueDraftInput = z.input<typeof SessionCatalogueDraftSchema>;

export const ListSessionCatalogueRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  ...PageQuerySchema.shape,
  ...TextFilterSchema.shape,
  ...sortBy(CATALOG_SORT_FIELDS).shape,
  kinds: queryList(CatalogKindSchema, z.array(CatalogKindSchema).max(5)).optional(),
});
export type ListSessionCatalogueRequest = z.infer<typeof ListSessionCatalogueRequestSchema>;

export const ListSessionCatalogueResponseSchema = z.strictObject({
  ...paginated(CatalogEntrySchema).shape,
  countsByKind: z.record(CatalogKindSchema, z.int().min(0)),
  /**
   * The namespace this session's entries are minted under, derived from the client
   * name. Returned so the form can show the id a name will produce *before* the
   * entry is created — the namespace is derived and not author-supplied, so
   * otherwise the first time anyone sees it is after the write.
   */
  namespace: ShortTextSchema,
});
export type ListSessionCatalogueResponse = z.infer<typeof ListSessionCatalogueResponseSchema>;

export const SessionCatalogueEntryResponseSchema = z.strictObject({ entry: CatalogEntrySchema });
export type SessionCatalogueEntryResponse = z.infer<typeof SessionCatalogueEntryResponseSchema>;

export const AddSessionCatalogueEntryRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  entry: SessionCatalogueDraftSchema,
  /**
   * The platform entry this one stands in for. Set once, at creation: a shadow is a
   * statement about why the entry exists, not a field an edit revises.
   */
  standsInFor: ElementIdSchema.optional(),
});
export type AddSessionCatalogueEntryRequest = z.infer<typeof AddSessionCatalogueEntryRequestSchema>;

/**
 * A session entry is addressed by its row uuid and its kind, not by its ontology id.
 *
 * The ontology id of a session entry carries the client namespace —
 * `tool:vanguard-retail-group/backstage-idp` — so it is not one path segment, and a
 * percent-encoded slash is a path that depends on every proxy in front of the API
 * agreeing not to normalise it. The uuid is already on every catalogue response as
 * `recordId`, and `kind` is what says which of the five tables to look in.
 */
const sessionEntryPath = {
  sessionId: IdentifierSchema,
  kind: CatalogKindSchema,
  recordId: IdentifierSchema,
} as const;

export const UpdateSessionCatalogueEntryRequestSchema = z.strictObject({
  ...sessionEntryPath,
  entry: SessionCatalogueDraftSchema,
});
export type UpdateSessionCatalogueEntryRequest = z.infer<
  typeof UpdateSessionCatalogueEntryRequestSchema
>;

export const RemoveSessionCatalogueEntryRequestSchema = z.strictObject({
  ...sessionEntryPath,
  ...DeleteCatalogEntryQuery,
});
export type RemoveSessionCatalogueEntryRequest = z.infer<
  typeof RemoveSessionCatalogueEntryRequestSchema
>;

export const BringGlobalEntryRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  kind: CatalogKindSchema,
  /** The platform entry to copy — `tool:github`, not a uuid. */
  id: ElementIdSchema,
  /** Rename the copy. Defaults to the platform entry's name. */
  name: LabelSchema.optional(),
});
export type BringGlobalEntryRequest = z.infer<typeof BringGlobalEntryRequestSchema>;

/* ── Promotion ─────────────────────────────────────────────────────────────
 *
 * The one path from a session catalogue back into the platform's. A facilitator who
 * finds that what a client brought is not client-specific at all *proposes* it; only
 * `catalog:manage` accepts, because accepting writes a row every future adoption will
 * see.
 *
 * Two-sided rather than one write, deliberately. The capability that lets a facilitator
 * author entries in their own room is not the capability that curates the platform, and
 * collapsing the two would make every session able to grow the global catalogue as a
 * side effect of tailoring.
 *
 * Accepting **rehomes the id**: `tool:vanguard-retail-group/backstage-idp` becomes
 * `tool:backstage-idp`, because a global row keyed under one client's namespace tells
 * every later reader that the platform's Backstage belongs to Vanguard. Nothing already
 * published moves — a variant froze the nodes it quoted at publish — and the session's
 * own row stays exactly where it is, now recording the accepted id as what it stands in
 * for. That record is the alias: anything still holding the client id resolves, and the
 * promotion row itself is the durable statement of which id became which.
 */

export const CataloguePromotionSchema = z.strictObject({
  /** The proposal's own row uuid, which is what the accept and decline routes address. */
  id: IdentifierSchema,
  state: PromotionStateSchema,
  kind: CatalogKindSchema,
  /** The session the entry was authored in, and enough of it to render a row. */
  sessionId: IdentifierSchema,
  sessionName: ShortTextSchema,
  clientName: LabelSchema.nullable(),
  /** The session entry: its ontology id, its row uuid and its name. */
  entryId: ElementIdSchema,
  entryRecordId: IdentifierSchema,
  entryName: LabelSchema,
  /**
   * The id this entry would take in the platform catalogue — the *Would become* column.
   *
   * Computed when the proposal is made, so the queue can show it, and recomputed on
   * acceptance: a global id taken in between is a collision that has to be resolved
   * against the catalogue as it is at the moment of the write, not as it was when the
   * facilitator proposed.
   */
  proposedId: ElementIdSchema,
  /** Why this belongs to everyone, in the facilitator's own words. */
  rationale: SentenceSchema,
  proposedAt: IsoDateTimeSchema,
  proposedBy: ShortTextSchema,
  decidedAt: IsoDateTimeSchema.nullable(),
  decidedBy: ShortTextSchema.nullable(),
  /** The author's note on the decision, which is the whole answer when it was a decline. */
  decisionNote: SentenceSchema.nullable(),
  /** The global id acceptance actually minted. Null until accepted, and null on a decline. */
  promotedId: ElementIdSchema.nullable(),
});
export type CataloguePromotion = z.infer<typeof CataloguePromotionSchema>;

export const ProposeCataloguePromotionRequestSchema = z.strictObject({
  ...sessionEntryPath,
  rationale: SentenceSchema,
});
export type ProposeCataloguePromotionRequest = z.infer<
  typeof ProposeCataloguePromotionRequestSchema
>;
export const ProposeCataloguePromotionResponseSchema = z.strictObject({
  promotion: CataloguePromotionSchema,
});
export type ProposeCataloguePromotionResponse = z.infer<
  typeof ProposeCataloguePromotionResponseSchema
>;

export const ListCataloguePromotionsRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  ...TextFilterSchema.shape,
  kinds: queryList(CatalogKindSchema, z.array(CatalogKindSchema).max(5)).optional(),
  states: queryList(PromotionStateSchema, z.array(PromotionStateSchema).max(3)).optional(),
  /** One session's proposals only — how the authoring screen filters by adoption. */
  sessionId: IdentifierSchema.optional(),
});
export type ListCataloguePromotionsRequest = z.infer<typeof ListCataloguePromotionsRequestSchema>;
export const ListCataloguePromotionsResponseSchema = z.strictObject({
  ...paginated(CataloguePromotionSchema).shape,
  /**
   * Counts over every state, not over the filtered page: the queue's badge says how many
   * proposals are waiting, and a badge that changed when somebody filtered the list would
   * be reporting the filter rather than the backlog.
   */
  countsByState: z.record(PromotionStateSchema, z.int().min(0)),
});
export type ListCataloguePromotionsResponse = z.infer<typeof ListCataloguePromotionsResponseSchema>;

export const AcceptCataloguePromotionRequestSchema = z.strictObject({
  promotionId: IdentifierSchema,
  /**
   * The id to give the platform entry, when the derived one is not what the author wants.
   *
   * Optional and rarely sent: the server derives `proposedId` from the entry's local part,
   * which is right nearly always. It is here because an author accepting
   * `tool:northwind-energy/backstage` may know the platform already calls that thing a
   * developer portal, and the moment to say so is the moment the id becomes permanent.
   */
  globalId: ElementIdSchema.optional(),
  note: SentenceSchema.optional(),
});
export type AcceptCataloguePromotionRequest = z.infer<typeof AcceptCataloguePromotionRequestSchema>;
export const AcceptCataloguePromotionResponseSchema = z.strictObject({
  promotion: CataloguePromotionSchema,
  /** The platform row acceptance wrote, so the list can show it without a second read. */
  entry: CatalogEntrySchema,
});
export type AcceptCataloguePromotionResponse = z.infer<
  typeof AcceptCataloguePromotionResponseSchema
>;

export const DeclineCataloguePromotionRequestSchema = z.strictObject({
  promotionId: IdentifierSchema,
  /**
   * Required, unlike the note on an acceptance. Most client entries should stay in their
   * session, so declining is the ordinary answer rather than a rejection — and the
   * facilitator who made the case is owed the reason it did not carry.
   */
  note: SentenceSchema,
});
export type DeclineCataloguePromotionRequest = z.infer<
  typeof DeclineCataloguePromotionRequestSchema
>;
export const DeclineCataloguePromotionResponseSchema = z.strictObject({
  promotion: CataloguePromotionSchema,
});
export type DeclineCataloguePromotionResponse = z.infer<
  typeof DeclineCataloguePromotionResponseSchema
>;
